import type { DictFragment } from '../translations';

/** Task board, task detail and reply UI strings. */
export const tasks: DictFragment = {
  fr: {
    // ── tasks. (board + create/detail view) ──
    'tasks.createTask': 'Créer une tâche',
    'tasks.createTaskBtn': 'Créer une tâche',
    'tasks.project': 'Projet',
    'tasks.selectProjectPlaceholder': 'Sélectionner un projet…',
    'tasks.statusColumn': 'Colonne de statut',
    'tasks.assignee': 'Assigné·e',
    'tasks.chooseMilestone': 'Aucun jalon',
    'tasks.milestoneUnknown': 'Ce jalon ne fait pas partie des jalons du projet.',
    'tasks.statusForbidden': 'Seul·e le ou la responsable de la tâche, le PMO ou l’accompagnant·e PMO du projet peut changer son statut.',
    'tasks.noMilestonesYet': 'Ce projet n’a pas encore de jalon : la tâche ne sera reliée à aucun jalon. Elle pourra y être rattachée une fois un jalon créé dans l’onglet Planification de la fiche projet.',
    'tasks.openProjectPlanning': 'Ouvrir la fiche projet',
    'tasks.milestoneMissingOnTask': 'Cette tâche n’est reliée à aucun jalon.',
    'tasks.unassigned': 'Non assigné·e',
    'tasks.titlePlaceholder': 'Titre de la tâche…',
    'tasks.titleBarePlaceholder': 'Titre de la tâche',
    'tasks.descriptionPlaceholder': 'Description facultative…',
    'tasks.describePlaceholder': 'Décrivez cette tâche…',
    'tasks.dueDate': 'Échéance',
    'tasks.dueDateAdminOnly': 'Seuls la PMO et les admins peuvent modifier l’échéance',
    'tasks.plannedDate': 'Date planifiée',
    'tasks.milestone': 'Jalon',
    'tasks.noMilestone': 'Aucun jalon',
    'tasks.openMilestone': 'Voir le détail du jalon',
    'tasks.cardMilestone': 'Jalon {n} · {name}',
    'tasks.cardMilestoneUntitled': 'Jalon {n}',
    'tasks.cardNoMilestone': "Cette tâche n'est liée à aucun jalon",
    'tasks.linkedProject': 'Projet lié à cette tâche',
    'tasks.linkedProjectPlaceholder': 'Aucun projet lié',
    'tasks.primaryProjectPrefix': 'Projet principal :',
    'tasks.presentInProjects': 'Présent dans {projects}',
    'tasks.readOnlyNotice': 'Lecture seule — modifiable uniquement depuis le projet principal.',
    'tasks.privateTask': 'Tâche privée',
    'tasks.privateTaskShort': 'Privée',
    'tasks.privacyLabel': 'Confidentialité',
    'tasks.publicTaskHelp': 'Visible et accessible à tous les membres du projet.',
    'tasks.privacyReadOnly': 'Seuls le créateur de la tâche, la personne assignée et le PMO peuvent changer ce réglage.',
    'tasks.contactCreator': 'Contacter {name}',
    'tasks.contactCreatorHelp': 'Envoyer une demande à {name}, qui a créé cette tâche',
    'tasks.contactCreatorFallback': 'le créateur',
    'tasks.contactCreatorTitle': 'Contacter {name} au sujet de cette tâche',
    'tasks.contactReasonLabel': 'Motif',
    'tasks.contactReason.precisions': 'Demande de précisions',
    'tasks.contactReason.question': 'Question',
    'tasks.contactReason.issue': 'Signaler un problème',
    'tasks.contactReason.other': 'Autre',
    'tasks.contactMessageLabel': 'Message',
    'tasks.contactMessagePlaceholder': 'Expliquez ce que vous souhaitez savoir…',
    'tasks.contactExplain': '{name} recevra une notification et un e-mail. Votre message est aussi ajouté au fil de la tâche.',
    'tasks.contactSent': 'Message envoyé à {name}.',
    'tasks.remind.button': 'Envoyer un rappel',
    'tasks.remind.help': 'Envoyer un rappel à {name} sur Slack et par e-mail',
    'tasks.remind.title': 'Rappel à {name}',
    'tasks.remind.noteLabel': 'Note (facultative)',
    'tasks.remind.notePlaceholder': 'Ce que vous voulez lui rappeler…',
    'tasks.remind.channelsLabel': 'Envoyer par',
    'tasks.remind.slack': 'Slack',
    'tasks.remind.email': 'E-mail',
    'tasks.remind.viaSlack': 'Slack',
    'tasks.remind.viaEmail': 'e-mail',
    'tasks.remind.and': ' et ',
    'tasks.remind.explain': '{name} reçoit la tâche, son échéance, votre note et un lien. Le rappel est noté dans le fil de la tâche.',
    'tasks.remind.sent': 'Rappel envoyé à {name} ({via}).',
    'tasks.remind.channelFailed': 'Pas envoyé par {channel} : {reason}',
    'tasks.remind.nothingSent': 'Rien n’a été envoyé.',
    'tasks.remind.err.noAssignee': 'Cette tâche n’a pas de responsable.',
    'tasks.remind.err.noChannel': 'Choisissez Slack, e-mail ou les deux.',
    'tasks.remind.err.noAccount': 'Aucune adresse e-mail n’est connue pour {name} (ni compte PMO, ni fiche dans Personnes).',
    'tasks.remind.err.slackDisabled': 'Slack n’est pas configuré ou est désactivé (Admin > Slack).',
    'tasks.remind.err.notOnSlack': 'Aucun compte Slack ne correspond à l’e-mail de {name}.',
    'tasks.remind.err.slackFailed': 'Slack a refusé le message (droits du bot ?).',
    'tasks.remind.err.emailFailed': 'L’e-mail n’a pas pu être mis en file d’envoi.',
    'tasks.remind.err.forbidden': 'Seul le PMO ou l’accompagnant·e PMO du projet peut envoyer un rappel.',
    'tasks.publicTask': 'Publique',
    'tasks.privateTaskHelp': 'Seuls son créateur, la personne assignée et le PMO peuvent l’ouvrir.',
    'tasks.privateLocked': 'Cette tâche est privée : seuls son créateur, la personne assignée et le PMO peuvent l’ouvrir.',
    'tasks.blockedReasonPlaceholder': 'Pourquoi cette tâche est-elle bloquée ?',
    'tasks.dependencies': 'Dépendances',
    'tasks.dependentOn': 'Dépend de',
    'tasks.enables': 'Permet',
    'tasks.selectDependsOn': 'Sélectionner la tâche dont celle-ci dépend',
    'tasks.selectEnables': 'Sélectionner la tâche que celle-ci permet',
    'tasks.noAvailableTasks': 'Aucune tâche disponible.',
    'tasks.noOtherTasks': 'Aucune autre tâche dans ce projet.',
    'tasks.createNewPlaceholder': 'Ou créer une nouvelle tâche…',
    'tasks.taskLabel': 'Tâche',
    'tasks.privateNotes': 'Notes privées',
    'tasks.privateNotesPlaceholder': 'Notes visibles par vous seul·e…',
    'tasks.deleteTask': 'Supprimer la tâche',
    'tasks.deleteConfirm': 'Supprimer cette tâche ? Cette action est irréversible.',
    'tasks.backToBoard': 'Retour au tableau',
    'tasks.migrateTask': 'Migrer vers un autre projet',
    'tasks.page.alsoIn': 'Aussi dans',
    'tasks.page.alsoInHelp': 'Autres projets où la tâche apparaît. Elle ne se modifie que depuis son projet principal.',
    'tasks.page.createdBy': 'Créée par',
    'tasks.migrateTargetLabel': 'Projet de destination',
    'tasks.migrateWarning': 'La tâche conserve son statut, ses dates et son historique. Ses dépendances sont retirées, car elles sont propres au projet actuel ; son échéance est ramenée à la fin du nouveau jalon si elle la dépasse.',
    'tasks.migrateMilestoneLabel': 'Jalon dans le projet de destination',
    'tasks.migrateConfirm': 'Migrer',
    'tasks.migrateNoMilestoneHint': 'Ce projet n’a pas encore de jalon : la tâche est déplacée sans jalon. Elle pourra être rattachée à un jalon plus tard.',
    'tasks.noMilestonesOnTask': 'Cette tâche n’est reliée à aucun jalon : ce projet n’en a pas encore. Créez-en un dans l’onglet Planification de la fiche projet pour l’y rattacher.',
    'tasks.migrateNoTargets': 'Aucun autre projet ne peut accueillir cette tâche pour le moment.',
    'tasks.backToProject': 'Retour au projet',
    'tasks.notFound': 'Tâche introuvable.',
    'tasks.noColumnsTitle': 'Aucune colonne de tâches configurée',
    'tasks.noColumnsDesc': 'Allez dans Admin → Naming pour ajouter des colonnes au tableau des tâches.',
    'tasks.filterProjects': 'Projets',
    'tasks.filterSponsor': 'Sponsor',
    'tasks.filterCollaborators': 'Collaborateurs',
    'tasks.filterPerson': 'Personne',
    'tasks.everyonePlaceholder': 'tout le monde…',
    'tasks.everyone': 'Tout le monde',
    'tasks.highlightMine': 'Mes tâches',
    'tasks.allProjectsPlaceholder': 'tous les projets…',
    'tasks.anySponsorPlaceholder': 'n’importe quel sponsor…',
    'tasks.anyPersonPlaceholder': 'n’importe quelle personne…',
    'tasks.emptyTitle': 'Aucune tâche pour le moment',
    'tasks.emptyDesc': 'Créez une tâche pour commencer.',
    'tasks.sponsorOption': '{name} (Sponsor)',
    'tasks.viewDetails': 'Voir les détails',

    // ── taskDetail. (planner modal + info side panel) ──
    'taskDetail.descriptionPlaceholder': 'Détails facultatifs…',
    'taskDetail.dueDate': 'Échéance',
    'taskDetail.dueDateAdminOnly': 'Seuls la PMO et les admins peuvent modifier l’échéance',
    'taskDetail.plannedDate': 'Date planifiée',
    'taskDetail.plannedTime': 'Heure planifiée',
    'taskDetail.assignee': 'Assigné·e',
    'taskDetail.unassigned': 'Non assigné·e',
    'taskDetail.milestone': 'Jalon',
    'taskDetail.milestoneDuePrefix': 'Fin le',
    'taskDetail.contributesToMilestone': 'Cette tâche contribue au jalon',
    'taskDetail.noMilestone': 'Aucun jalon',
    'taskDetail.privateNotes': 'Notes privées',
    'taskDetail.privateNotesPlaceholder': 'Notes pour l’équipe du projet…',
    'taskDetail.column': 'Colonne',
    'taskDetail.colBacklog': 'Backlog',
    'taskDetail.colTodo': 'À faire',
    'taskDetail.colInProgress': 'En cours',
    'taskDetail.colDone': 'Terminé',
    'taskDetail.colArchived': 'Archivées',
    'taskDetail.openFullDetail': 'Ouvrir le détail complet',
    'taskDetail.metaDueDate': 'Échéance',
    'taskDetail.metaPlannedDate': 'Date planifiée',
    'taskDetail.notSet': 'Non défini',
    'taskDetail.createdBy': 'Créé par',
    'taskDetail.dependencies': 'Dépendances',
    'taskDetail.dependentOn': 'Dépend de',
    'taskDetail.enables': 'Permet',
    'taskDetail.activity': 'Activité',

    // ── reply. (inline reply form + mentions) ──
    'reply.projectMembers': 'Membres du projet',
    'reply.directionAdmin': 'Direction et admin',
    'reply.replyingTo': 'Réponse à {name}',
    'reply.placeholder': 'Écrire une réponse…',
    'reply.reply': 'Répondre',
  },
  en: {
    // ── tasks. ──
    'tasks.createTask': 'Create Task',
    'tasks.createTaskBtn': 'Create task',
    'tasks.project': 'Project',
    'tasks.selectProjectPlaceholder': 'Select project...',
    'tasks.statusColumn': 'Status Column',
    'tasks.assignee': 'Assignee',
    'tasks.chooseMilestone': 'No milestone',
    'tasks.milestoneUnknown': 'This milestone is not one of the project milestones.',
    'tasks.statusForbidden': 'Only the task’s responsible, the PMO or the project’s PMO accompanist can change its status.',
    'tasks.noMilestonesYet': 'This project has no milestone yet: the task won’t be linked to any milestone. It can be attached to one once a milestone is created on the project’s Planning tab.',
    'tasks.openProjectPlanning': 'Open the project',
    'tasks.milestoneMissingOnTask': 'This task is not linked to any milestone.',
    'tasks.unassigned': 'Unassigned',
    'tasks.titlePlaceholder': 'Task title...',
    'tasks.titleBarePlaceholder': 'Task title',
    'tasks.descriptionPlaceholder': 'Optional description...',
    'tasks.describePlaceholder': 'Describe this task...',
    'tasks.dueDate': 'Due Date',
    'tasks.dueDateAdminOnly': 'Only PMO and admins can change the due date',
    'tasks.plannedDate': 'Planned Date',
    'tasks.milestone': 'Milestone',
    'tasks.noMilestone': 'No milestone',
    'tasks.openMilestone': 'View milestone details',
    'tasks.cardMilestone': 'Milestone {n} · {name}',
    'tasks.cardMilestoneUntitled': 'Milestone {n}',
    'tasks.cardNoMilestone': 'This task is not linked to any milestone',
    'tasks.linkedProject': 'Project linked to this task',
    'tasks.linkedProjectPlaceholder': 'No linked project',
    'tasks.primaryProjectPrefix': 'Home project:',
    'tasks.presentInProjects': 'Present in {projects}',
    'tasks.readOnlyNotice': 'Read-only — editable only from the home project.',
    'tasks.privateTask': 'Private task',
    'tasks.privateTaskShort': 'Private',
    'tasks.privacyLabel': 'Privacy',
    'tasks.publicTaskHelp': 'Visible and open to every project member.',
    'tasks.privacyReadOnly': 'Only the task creator, its assignee and the PMO can change this.',
    'tasks.contactCreator': 'Contact {name}',
    'tasks.contactCreatorHelp': 'Send a request to {name}, who created this task',
    'tasks.contactCreatorFallback': 'the creator',
    'tasks.contactCreatorTitle': 'Contact {name} about this task',
    'tasks.contactReasonLabel': 'Reason',
    'tasks.contactReason.precisions': 'Request for details',
    'tasks.contactReason.question': 'Question',
    'tasks.contactReason.issue': 'Report a problem',
    'tasks.contactReason.other': 'Other',
    'tasks.contactMessageLabel': 'Message',
    'tasks.contactMessagePlaceholder': 'Explain what you would like to know…',
    'tasks.contactExplain': '{name} will get a notification and an e-mail. Your message is also added to the task thread.',
    'tasks.contactSent': 'Message sent to {name}.',
    'tasks.remind.button': 'Send a reminder',
    'tasks.remind.help': 'Send {name} a reminder on Slack and by e-mail',
    'tasks.remind.title': 'Reminder to {name}',
    'tasks.remind.noteLabel': 'Note (optional)',
    'tasks.remind.notePlaceholder': 'What you want to remind them of…',
    'tasks.remind.channelsLabel': 'Send by',
    'tasks.remind.slack': 'Slack',
    'tasks.remind.email': 'E-mail',
    'tasks.remind.viaSlack': 'Slack',
    'tasks.remind.viaEmail': 'e-mail',
    'tasks.remind.and': ' and ',
    'tasks.remind.explain': '{name} gets the task, its due date, your note and a link. The reminder is noted in the task’s thread.',
    'tasks.remind.sent': 'Reminder sent to {name} ({via}).',
    'tasks.remind.channelFailed': 'Not sent by {channel}: {reason}',
    'tasks.remind.nothingSent': 'Nothing was sent.',
    'tasks.remind.err.noAssignee': 'This task has no responsible.',
    'tasks.remind.err.noChannel': 'Choose Slack, e-mail or both.',
    'tasks.remind.err.noAccount': 'No e-mail address is known for {name} (no PMO account, no entry in People).',
    'tasks.remind.err.slackDisabled': 'Slack is not set up or is turned off (Admin > Slack).',
    'tasks.remind.err.notOnSlack': 'No Slack account matches {name}’s e-mail.',
    'tasks.remind.err.slackFailed': 'Slack refused the message (bot permissions?).',
    'tasks.remind.err.emailFailed': 'The e-mail could not be queued for sending.',
    'tasks.remind.err.forbidden': 'Only the PMO or the project’s PMO accompanist can send a reminder.',
    'tasks.publicTask': 'Public',
    'tasks.privateTaskHelp': 'Only its creator, its assignee and the PMO can open it.',
    'tasks.privateLocked': 'This task is private: only its creator, its assignee and the PMO can open it.',
    'tasks.blockedReasonPlaceholder': 'Why is this task blocked?',
    'tasks.dependencies': 'Dependencies',
    'tasks.dependentOn': 'Dependent On',
    'tasks.enables': 'Enables',
    'tasks.selectDependsOn': 'Select task this depends on',
    'tasks.selectEnables': 'Select task this enables',
    'tasks.noAvailableTasks': 'No available tasks.',
    'tasks.noOtherTasks': 'No other tasks in this project.',
    'tasks.createNewPlaceholder': 'Or create a new task...',
    'tasks.taskLabel': 'Task',
    'tasks.privateNotes': 'Private Notes',
    'tasks.privateNotesPlaceholder': 'Notes visible only to you...',
    'tasks.deleteTask': 'Delete Task',
    'tasks.deleteConfirm': 'Delete this task? This cannot be undone.',
    'tasks.backToBoard': 'Back to Board',
    'tasks.migrateTask': 'Move to another project',
    'tasks.page.alsoIn': 'Also in',
    'tasks.page.alsoInHelp': 'Other projects where the task appears. It is edited only from its home project.',
    'tasks.page.createdBy': 'Created by',
    'tasks.migrateTargetLabel': 'Target project',
    'tasks.migrateWarning': 'The task keeps its status, dates and history. Its dependencies are removed, as they belong to the current project; its due date is pulled back to the new milestone’s end if it runs past it.',
    'tasks.migrateMilestoneLabel': 'Milestone in the target project',
    'tasks.migrateConfirm': 'Move',
    'tasks.migrateNoMilestoneHint': 'This project has no milestone yet: the task is moved without one. It can be attached to a milestone later.',
    'tasks.noMilestonesOnTask': 'This task is not linked to any milestone: this project has none yet. Create one on the project’s Planning tab to attach it.',
    'tasks.migrateNoTargets': 'No other project can take this task at the moment.',
    'tasks.backToProject': 'Back to project',
    'tasks.notFound': 'Task not found.',
    'tasks.noColumnsTitle': 'No task columns configured',
    'tasks.noColumnsDesc': 'Go to Admin → Naming to add task board columns.',
    'tasks.filterProjects': 'Projects',
    'tasks.filterSponsor': 'Sponsor',
    'tasks.filterCollaborators': 'Collaborators',
    'tasks.filterPerson': 'Person',
    'tasks.everyonePlaceholder': 'everyone…',
    'tasks.everyone': 'Everyone',
    'tasks.highlightMine': 'My tasks',
    'tasks.allProjectsPlaceholder': 'all projects…',
    'tasks.anySponsorPlaceholder': 'any sponsor…',
    'tasks.anyPersonPlaceholder': 'any person…',
    'tasks.emptyTitle': 'No tasks yet',
    'tasks.emptyDesc': 'Create a task to get started.',
    'tasks.sponsorOption': '{name} (Sponsor)',
    'tasks.viewDetails': 'View details',

    // ── taskDetail. ──
    'taskDetail.descriptionPlaceholder': 'Optional details…',
    'taskDetail.dueDate': 'Due date',
    'taskDetail.dueDateAdminOnly': 'Only PMO and admins can change the due date',
    'taskDetail.plannedDate': 'Planned date',
    'taskDetail.plannedTime': 'Planned time',
    'taskDetail.assignee': 'Assignee',
    'taskDetail.unassigned': 'Unassigned',
    'taskDetail.milestone': 'Milestone',
    'taskDetail.milestoneDuePrefix': 'Due',
    'taskDetail.contributesToMilestone': 'This task contributes to milestone',
    'taskDetail.noMilestone': 'No milestone',
    'taskDetail.privateNotes': 'Private notes',
    'taskDetail.privateNotesPlaceholder': 'Notes for the project team…',
    'taskDetail.column': 'Column',
    'taskDetail.colBacklog': 'Backlog',
    'taskDetail.colTodo': 'To Do',
    'taskDetail.colInProgress': 'In Progress',
    'taskDetail.colDone': 'Done',
    'taskDetail.colArchived': 'Archived',
    'taskDetail.openFullDetail': 'Open full detail',
    'taskDetail.metaDueDate': 'Due Date',
    'taskDetail.metaPlannedDate': 'Planned Date',
    'taskDetail.notSet': 'Not set',
    'taskDetail.createdBy': 'Created by',
    'taskDetail.dependencies': 'Dependencies',
    'taskDetail.dependentOn': 'Dependent On',
    'taskDetail.enables': 'Enables',
    'taskDetail.activity': 'Activity',

    // ── reply. ──
    'reply.projectMembers': 'Project Members',
    'reply.directionAdmin': 'Direction & Admin',
    'reply.replyingTo': 'Replying to {name}',
    'reply.placeholder': 'Write a reply...',
    'reply.reply': 'Reply',
  },
};
